// IMPORT REACT
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { format, isValid } from "date-fns";

// IMPORT CSS
import "../../theme/style.scss";
import "react-phone-number-input/style.css";

// IMPORT UI COMPONENTS
import {
  Box,
  FormControl,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import SkeletonWrapper from "../../components/Skeleton/SkeletonWrapper";
import { CustomTextField } from "../../components/CustomFields/TextField";
import DragDropFile from "../../components/DragDropFile/DragDropFile";

// IMPORT SERVICE
import { AlertType } from "../../services/models/AlertType";
import { EncryptDecryptService } from "../../services/common/EncryptionService";
import { PreferencesContext } from "../../PreferenceContext";
import DatePickerComponent from "../CustomFields/DatePicker";
import { DropZone } from "../CustomFields/DropZone";
import LoadingButton from "@mui/lab/LoadingButton";
import dayjs from "dayjs";
import { popTeamMemberDocId } from "../../Helpers/typings";
import { MultiUpload } from "../MultiUpload/MultiUpload";
//reject for file remove
import { isEmpty, reject } from "lodash";
import { CreateTeamMemberDocument, GetTeamMemberDocumentDetail, UpdateTeamMemberDocument } from "../../services/TeamMemberDocumentService";
// SCHEMA FOR OPTION
interface selectOption {
  label: string;
  value: string;
}

// SCHEMA FOR FORM
interface formInitialValueTypes {
  specialityId: number;
  departmentId: number;
  branchId: number;
  orgId: number;
  consumerId: number;
  teamMemberId: number;
  teamMemberDocumentId: number;
  registrationNumber: string;
  registrationDate: string;
  expirationDate: string;
  documentName: string;
  description: string;
  folderPath: string;
  file: any[];
  fileUpdateName: string;
  DocumentUploadMode: string;
  DocumentOriginalName: string;
  DocumentServerName: string;
  isDeleted: boolean;
}

// DEFAULT SCHEMA VALUE FOR FORM
const initTeamMemberDocumentData: formInitialValueTypes = {
  specialityId: 0,
  departmentId: 0,
  branchId: 0,
  orgId: 0,
  consumerId: 0,
  teamMemberId: 0,
  teamMemberDocumentId: 0,
  registrationNumber: "",
  registrationDate: "",
  expirationDate: "",
  documentName: "",
  description: "",
  folderPath: "",
  file: [],
  fileUpdateName: "",
  DocumentUploadMode: "I",
  DocumentOriginalName: "",
  DocumentServerName: "",
  isDeleted: false,
};

export type Props = {
  popUpteamMemberDocId: popTeamMemberDocId;
};

export default function TeamMemberDocumentSave(Props: any) {
  // PROPS
  const {
    isForPopUp = false,
    popUpteamMemberDocId,
    popupTeamMemberId,
    handleClosePopUp,
  } = Props;

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }
  const [TeamMemberDocumentData, setTeamMemberDocumentData] = useState(
    initTeamMemberDocumentData
  );
  const [deletedFilesId, setDeletedFilesId] = useState<any>([]);
  const [deletedFilesName, setDeletedFilesName] = useState<any>([]);
  const [organizationName, setOraganizationName] = useState("");
  const [documentsFiles, setDocumentsFile] = useState<any>([]);
  const saveLogoFileSelected = (files: any) => {
    setDocumentsFile(files);
  };
  function dataURLtoFile(dataurl, filename) {
    var arr = dataurl.split(","),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }

  const dowloadDocumentFile = (files: { name: any }) => {
    const selectedFile = documentsFiles.find(
      (doc: { name: any }) => files?.name === doc?.name
    );

    if (selectedFile && selectedFile.preview) {
      const downloadLink = document.createElement("a");
      downloadLink.href = selectedFile.preview; // Changed from `documentFile` to `preview` based on your data structure
      downloadLink.download = selectedFile.name; // Changed from `fileOriginalName` to `name` based on your data structure
      downloadLink.click();
    }
  };
  // GETTTING PARAMS
  let urlParams = useQuery();
  let curRawTeamMemberDocumentId = urlParams.get("q");
  let curTeamMemberDocumentId = 0;
  if (!isForPopUp) {
    if (curRawTeamMemberDocumentId !== null && curRawTeamMemberDocumentId !== undefined) {
      let decId = edService.decrypt(curRawTeamMemberDocumentId);
      if (decId !== "" && decId !== null && decId !== undefined) {
        curTeamMemberDocumentId = +decId;
      }
    } else {
      navigate("/not-found");
    }
  } else {
    curTeamMemberDocumentId = popUpteamMemberDocId;
  }
  const removeFile = (file: { name: any }) => {
    const updatedFiles = documentsFiles.filter(
      (doc: { name: any }) => doc.name !== file?.name
    );
    setDocumentsFile(updatedFiles);
    // if (curTeamMemberDocumentId > 0) {
    //   methods.reset({
    //     ...TeamMemberDocumentData,
    //     // purchaseDocCollectionOnUpdateData: tempDeletedIds,
    //     file: updatedFiles,

    //   });
    // }
  };
  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    specialityId: Yup.number().nullable(),
    departmentId: Yup.number().nullable(),
    branchId: Yup.number().nullable(),
    orgId: Yup.number().required(),
    consumerId: Yup.number().required(),
    teamMemberId: Yup.number().nullable(),
    teamMemberDocumentId: Yup.number().nullable(),
    registrationNumber: Yup.string().required(
      "Registration Number is required"
    ),
    registrationDate: Yup.string().nullable(),
    expirationDate: Yup.string().nullable(),
    documentName: Yup.string().required("Name is required"),
    description: Yup.string(),
    // file: Yup.string().required("File is required"),
    folderPath: Yup.string(),
    DocumentOriginalName: Yup.string(),
    DocumentServerName: Yup.string(),

    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: TeamMemberDocumentData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });
  // GET TeamMemberDocument DETAIL
  const getTeamMemberDocumentDetailById = async () => {
    let TeamMemberDocument: any;
    let params = new URLSearchParams();
    params.append("teamMemberDocumentId", curTeamMemberDocumentId.toString());
    params.append("teamMemberId", popupTeamMemberId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString()); // TEMP ONLY REMOVE AFTER PIN FUNCTIONALITY COMPLETE
    params.append("orgId", userContext?.organizationId?.toString()); // TEMP ONLY REMOVE AFTER PIN
    params.append("branchId", userContext?.branchId?.toString()); // TEMP ONLY REMOVE AFTER PIN
    try {
      const res: any = await GetTeamMemberDocumentDetail(params);
      TeamMemberDocument = res.data.data;
      methods.reset({
        ...initTeamMemberDocumentData,
        teamMemberDocumentId: TeamMemberDocument?.teamMemberDocumentId || 0,
        teamMemberId: TeamMemberDocument?.teamMemberId || 0,
        departmentId: TeamMemberDocument?.departmentId || 0,
        specialityId: TeamMemberDocument?.specialityId || 0,
        branchId: TeamMemberDocument?.branchId || 0,
        orgId: TeamMemberDocument?.orgId || 0,
        consumerId: TeamMemberDocument?.consumerId || 0,
        registrationNumber: TeamMemberDocument?.registrationNumber || "",
        registrationDate: TeamMemberDocument?.registrationDate || "",
        expirationDate: TeamMemberDocument?.expirationDate || "",
        documentName: TeamMemberDocument?.documentName || "",
        description: TeamMemberDocument?.description || "",
        folderPath: TeamMemberDocument?.folderPath || "",
        DocumentOriginalName: TeamMemberDocument?.DocumentOriginalName || "",
        DocumentServerName: TeamMemberDocument?.DocumentServerName || "",
        file: [TeamMemberDocument?.getDocumentFile],
      });
      if (TeamMemberDocument?.getDocumentFile) {
        const fileName = TeamMemberDocument?.originalFileName;
        const documentfile = dataURLtoFile(
          TeamMemberDocument?.getDocumentFile,
          fileName
        );
        //setShowDocument(TeamMemberDocument.getDocumentFile);
        setDocumentsFile([documentfile]);
      }
      // setDocumentsFile(TeamMemberDocument?.getDocumentFile);
      return TeamMemberDocument;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE BRANCH
  const saveTeamMemberDocument = async (value: formInitialValueTypes) => {
    try {
      let result: any = "";
      let formData: any = new FormData();
      formData.append("teamMemberDocumentId", value.teamMemberDocumentId);
      formData.append("teamMemberId", popupTeamMemberId?.toString());
      formData.append("consumerId", userContext.consumerId);
      formData.append("orgId", userContext.organizationId);
      formData.append("branchId", userContext.branchId);
      formData.append("registrationNumber", value.registrationNumber);
      formData.append("registrationDate", value.registrationDate);
      formData.append("expirationDate", value.expirationDate);
      formData.append("documentName", value.documentName);
      formData.append("description", value.description);
      formData.append("documentFile", documentsFiles[0]);
      formData.append("DocumentUploadMode", value.DocumentUploadMode);
      formData.append("DocumentOriginalName", value.DocumentOriginalName);
      formData.append("DocumentServerName", value.DocumentServerName);
      formData.append("dataManagedBy", userContext.loginDetail.userId);
      if (curTeamMemberDocumentId > 0) {
        result = await UpdateTeamMemberDocument(formData);
      } else {
        result = await CreateTeamMemberDocument(formData);
      }
      if (result?.data) {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
        if (!isForPopUp) {
          navigate(-1);
        } else {
          handleClosePopUp("CREATE_OR_UPDATE");
        }
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };
  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    saveTeamMemberDocument(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (!isForPopUp) {
      navigate(-1);
    } else {
      handleClosePopUp("CLOSE");
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      if (curTeamMemberDocumentId > 0) {
        const TeamMemberDocument = await getTeamMemberDocumentDetailById();
        setIsLoading(false);
      }
    } catch (e) {
      setIsLoading(false);
      console.log(e);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(false);
    // fetchData();
    if (curTeamMemberDocumentId > 0) {
      getTeamMemberDocumentDetailById();
      //setIsLoading(false);
    }
  }, [curTeamMemberDocumentId]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50); // Delay unmounting the Skeleton
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <Box>
      <Box className={"main-container"}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} className={"group-container"}>
                <Grid item container xs={12} spacing={2}>
                  <Grid item xs={12} sm={12} md={12}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.registrationNumber
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Registration Number
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder=""
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          size="small"
                          {...methods.register("registrationNumber")}
                          error={!!methods.formState.errors.registrationNumber}
                          className={`text-input ${
                            methods.formState.errors.registrationNumber
                              ? "error"
                              : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.registrationNumber?.message}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.registrationDate
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Registration / Birth Date
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <DatePickerComponent
                          format="DD-MM-YYYY"
                          disabled={false}
                          mode="date"
                          slotProps={{ textField: { size: "small" } }}
                          value={
                            methods?.getValues()?.registrationDate
                              ? dayjs(methods?.getValues()?.registrationDate)
                              : null
                          }
                          onChange={(e: any) => {
                            if (e !== null && isValid(e.$d)) {
                              let savedDateFormate: any = format(
                                e.$d,
                                "yyyy-MM-dd"
                              );
                              methods.setValue(
                                "registrationDate",
                                savedDateFormate
                              );
                            } else {
                              methods.setValue("registrationDate", "");
                            }
                          }}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.expirationDate
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Expiration Date
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <DatePickerComponent
                          format="DD-MM-YYYY"
                          disabled={false}
                          mode="date"
                          slotProps={{ textField: { size: "small" } }}
                          value={
                            methods?.getValues()?.expirationDate
                              ? dayjs(methods?.getValues()?.expirationDate)
                              : null
                          }
                          onChange={(e: any) => {
                            if (e !== null && isValid(e.$d)) {
                              let savedDateFormate: any = format(
                                e.$d,
                                "yyyy-MM-dd"
                              );
                              methods.setValue(
                                "expirationDate",
                                savedDateFormate
                              );
                            } else {
                              methods.setValue("expirationDate", "");
                            }
                          }}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={12} md={12}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.documentName
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Name
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder=""
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          size="small"
                          {...methods.register("documentName")}
                          error={!!methods.formState.errors.documentName}
                          className={`text-input ${
                            methods.formState.errors.documentName ? "error" : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.documentName?.message}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} md={12}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.description
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Description
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <TextField
                          placeholder=""
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          multiline
                          // maxRows={4}
                          rows={4}
                          size="small"
                          {...methods.register("description")}
                          error={!!methods.formState.errors.description}
                          className={`text-input ${
                            methods.formState.errors.description ? "error" : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.description?.message}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={12}>
                    <MultiUpload
                      onDropandSelect={saveLogoFileSelected}
                      editFile={documentsFiles}
                      MAX_COUNT={5}
                      onFileDownload={dowloadDocumentFile}
                      onFileRemove={(file: any) => {
                        removeFile(file);
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={showSkeleton}
                  >
                    {curTeamMemberDocumentId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={handleBack}
                  >
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
}
